import { useNavigate, useParams } from "react-router-dom";

import { createChannel, type ChannelInput } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { useZodForm, z, nonEmpty } from "@/lib/validation";
import { useToast } from "@/components/ui/toast";
import { CreatePage, SingleFooter, Section } from "@/components/wizard";

// Secrets are per-kind so they're optional in the base schema and required
// conditionally via superRefine, keyed off the selected channel kind.
const schema = z
  .object({
    name: nonEmpty("Name").max(64, "Keep it under 64 characters"),
    kind: z.enum(["telegram", "slack"]),
    role: z.enum(["operator", "agent"]),
    botToken: z.string().trim().default(""),
    chatId: z.string().trim().default(""),
    signingSecret: z.string().trim().default(""),
    slackChannel: z.string().trim().default(""),
  })
  .superRefine((v, ctx) => {
    const need = (path: keyof typeof v, label: string) => {
      if (!v[path]) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: `${label} is required` });
      }
    };
    if (v.kind === "telegram") {
      need("botToken", "Bot token");
      need("chatId", "Chat ID");
    } else {
      need("botToken", "Bot token");
      need("signingSecret", "Signing secret");
      need("slackChannel", "Channel");
    }
  });
type Values = z.infer<typeof schema>;

const SELECT_CLS =
  "h-9 w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-70";

export function CreateChannel() {
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const backTo = `/agents/${projectId}/settings/channels`;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useZodForm(schema, {
    defaultValues: {
      name: "",
      kind: "telegram",
      role: "operator",
      botToken: "",
      chatId: "",
      signingSecret: "",
      slackChannel: "",
    },
  });

  const kind = watch("kind");

  async function onSubmit(values: Values) {
    try {
      const body: ChannelInput = {
        name: values.name,
        kind: values.kind,
        role: values.role === "agent" ? "agent" : "",
      };
      if (values.kind === "telegram") {
        body.telegram = { bot_token: values.botToken, chat_id: values.chatId };
      } else {
        body.slack = {
          bot_token: values.botToken,
          signing_secret: values.signingSecret,
          channel: values.slackChannel,
        };
      }
      await createChannel(projectId, body);
      toast.success("Channel created.");
      navigate(backTo);
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't create the channel"));
    }
  }

  return (
    <CreatePage
      title="New channel"
      description="A Telegram or Slack channel — either for approval/notification alerts, or as a two-way chat where customers talk to this agent."
      backTo={backTo}
      footer={
        <SingleFooter
          onCancel={() => navigate(backTo)}
          onPrimary={() => void handleSubmit(onSubmit)()}
          submitting={isSubmitting}
          submitLabel="Create channel"
        />
      }
    >
      <Section title="Channel" description="Pick the provider and what this channel is used for.">
        <Field label="Type" htmlFor="ch-kind">
          <select id="ch-kind" className={SELECT_CLS} {...register("kind")}>
            <option value="telegram">Telegram</option>
            <option value="slack">Slack</option>
          </select>
        </Field>
        <Field
          label="Use"
          htmlFor="ch-role"
          hint={
            kind === "telegram"
              ? "Agent conversation = customers DM the bot (or reply in the group) and the platform-run agent answers; reply to its messages to resume an escalation."
              : "Agent conversation = customers post in the Slack channel and the agent answers in-thread."
          }
        >
          <select id="ch-role" className={SELECT_CLS} {...register("role")}>
            <option value="operator">Operator alerts (approvals & notifications)</option>
            <option value="agent">Agent conversation (two-way customer chat)</option>
          </select>
        </Field>
        <Field label="Name" htmlFor="ch-name" error={errors.name?.message} hint="e.g. on-call telegram">
          <Input id="ch-name" autoFocus placeholder="on-call telegram" aria-invalid={!!errors.name} {...register("name")} />
        </Field>
      </Section>

      {kind === "telegram" ? (
        <Section
          title="Telegram credentials"
          description="Create a bot with @BotFather, then paste its token and the target chat ID. Stored encrypted; never shown again."
        >
          <Field label="Bot token" htmlFor="ch-token" error={errors.botToken?.message}>
            <Input
              id="ch-token"
              type="password"
              placeholder="123456:ABC-DEF…"
              aria-invalid={!!errors.botToken}
              {...register("botToken")}
            />
          </Field>
          <Field
            label="Chat ID"
            htmlFor="ch-chat"
            error={errors.chatId?.message}
            hint="The numeric user/group/channel id the bot posts to (not an @username) — it must match exactly for inbound replies to correlate."
          >
            <Input id="ch-chat" placeholder="-1001234567890" aria-invalid={!!errors.chatId} {...register("chatId")} />
          </Field>
        </Section>
      ) : (
        <Section
          title="Slack credentials"
          description="From your Slack app: the Bot User OAuth token, the app's Signing Secret (verifies inbound events), and the target channel id. Stored encrypted; never shown again."
        >
          <Field label="Bot token" htmlFor="ch-token" error={errors.botToken?.message} hint="Starts with xoxb-">
            <Input
              id="ch-token"
              type="password"
              placeholder="xoxb-…"
              aria-invalid={!!errors.botToken}
              {...register("botToken")}
            />
          </Field>
          <Field label="Signing secret" htmlFor="ch-sign" error={errors.signingSecret?.message}>
            <Input
              id="ch-sign"
              type="password"
              placeholder="32-char hex"
              aria-invalid={!!errors.signingSecret}
              {...register("signingSecret")}
            />
          </Field>
          <Field label="Channel" htmlFor="ch-slack-ch" error={errors.slackChannel?.message} hint="The channel id the bot posts to, e.g. C0123456789.">
            <Input id="ch-slack-ch" placeholder="C0123456789" aria-invalid={!!errors.slackChannel} {...register("slackChannel")} />
          </Field>
        </Section>
      )}
    </CreatePage>
  );
}
